<template>
  <SectionContainer :background-variant="backgroundVariant" :colors="colors" class="transition-all duration-500 w-full">
    <div v-if="!messageSubmitted" :style="cssVars" class="flex flex-col w-full transition-all duration-500 items-center justify-center px-2 sm:px-0">
      <div class="max-w-2xl w-full" :class="computedCardClass">
        <component :is="headingTag" class="text-3xl font-bold text-center text-[var(--text-primary)]">
          {{ title }}
        </component>
        <p class="text-lg text-[var(--text-secondary)] text-center mt-2">
          {{ description }}
        </p>

        <form @submit.prevent="handleSubmit" class="mt-4 space-y-3">
          <!-- Name Field -->
          <div>
            <label class="block font-semibold text-[var(--text-primary)]">Your Name</label>
            <input
                v-model="formData.name"
                type="text"
                class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent)] bg-[var(--input-bg)] text-[var(--text-primary)]"
                placeholder="How should we address you?"
                @blur="validateName"
            />
            <p v-if="errors.name" class="text-red-500 text-sm mt-1">{{ errors.name }}</p>
          </div>

          <!-- Contact Field -->
          <div>
            <label class="block font-semibold text-[var(--text-primary)]">Contact Details</label>
            <input
                v-model="formData.contact"
                type="text"
                class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent)] bg-[var(--input-bg)] text-[var(--text-primary)]"
                placeholder="How can we reach you? Enter your phone or email."
                @blur="validateContact"
            />
            <p v-if="errors.contact" class="text-red-500 text-sm mt-1">{{ errors.contact }}</p>
          </div>

          <div>
            <label class="block font-semibold text-[var(--text-primary)]">Message</label>
            <textarea
                v-model="formData.message"
                rows="4"
                maxlength="500"
                class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent)] bg-[var(--input-bg)] text-[var(--text-primary)]"
                placeholder="What would you like to tell us?"
            />
          </div>

          <input
              v-model="formData.businessName"
              type="text"
              name="contact_subject"
              autocomplete="off"
              tabindex="-1"
              aria-hidden="true"
              class="businessName"
          />

          <!-- Request Quote Checkbox -->
          <div v-if="false" class="flex items-center gap-3">
            <input v-model="formData.requestQuote" type="checkbox" id="quote" class="w-5 h-5">
            <label for="quote" class="text-[var(--text-primary)]">I would like a quote</label>
          </div>

          <!-- Submit Button -->
          <div class="w-full flex items-center justify-center">
            <WebButton type="submit" :disabled="loading">
              {{ loading ? "Submitting..." : "Submit Request" }}
            </WebButton>
          </div>
        </form>
      </div>
    </div>
    <div v-else-if="isPreview" class="w-full flex items-center transition-all duration-500 justify-center">
      <div class="max-w-2xl p-10 w-full" :class="computedCardClass">
        <h2 class="text-3xl font-bold text-center text-[var(--text-primary)]">
          Thank you!
        </h2>
        <p class="text-lg text-[var(--text-secondary)] text-center mt-2">
          Your message has been submitted. We'll get back to you soon.
        </p>
        <p class="text-sm text-red-700 text-center mt-4">You are in Preview Mode.  Not message was sent, the above is what someone will see when they submit the form.</p>
      </div>
    </div>
    <div v-else class="w-full flex items-center transition-all duration-500 justify-center">
      <div class="max-w-2xl p-10 w-full" :class="computedCardClass">
        <h2 class="text-3xl font-bold text-center text-[var(--text-primary)]">
          Thank you!
        </h2>
        <p class="text-lg text-[var(--text-secondary)] text-center mt-2">
          Your message has been submitted. We'll get back to you soon.
        </p>
      </div>
    </div>
  </SectionContainer>
</template>

<style scoped>
.businessName {
  position: absolute;
  left: -9999px;
  top: -9999px;
  opacity: 0;
  height: 0;
  width: 0;
  pointer-events: none;
}
</style>

<script setup>
import {useSectionHeadingTag} from "@/composables/useSectionHeadingTag.js";
import { computed, ref, onMounted } from "vue";
import axios from "axios";
import WebButton from "@/components/WebButton.vue";
import SectionContainer from "@/components/SectionContainer.vue";
import router from "@/router/index.js";
const headingTag = useSectionHeadingTag();

const props = defineProps({
  title: { type: String, default: "Get in Touch" },
  description: {
    type: String,
    default: "We'd love to hear from you. Fill in your details and we'll get back to you soon."
  },
  colors: { type: Object, required: true },
  backgroundVariant: { type: Number, default: 1 }
});

// Form Data
const formData = ref({
  name: "",
  contact: "",
  requestQuote: false,
  message: "",
  businessName: ""
});


// Validation Errors
const errors = ref({
  name: "",
  contact: "",
  token: ""
});


const isPreview = ref(false);

if (typeof window !== 'undefined') {
  isPreview.value = import.meta.env.VITE_IS_PREVIEW_SERVER === 'true'
}
else {
  isPreview.value = process.env.IS_PREVIEW_SERVER === 'true'
}

// Security Token
const token = ref("");
const loading = ref(false);
const messageSubmitted = ref(false);

// Fetch a New Token from Backend
const getToken = async () => {
  try {
    const response = await axios.get("/api/contact/generate-token");
    token.value = response.data.token;
  } catch (error) {
    console.error("Failed to get security token:", error);
    errors.value.token = "Unable to get security token. Please try again.";
  }
};

// Fetch token when form loads
onMounted(async () => {
  // "Get a quote like this" on a project page (web v2) sends ?about=…&project=<slug>.
  try {
    const q = new URLSearchParams(window.location.search);
    const about = (q.get("about") || "").slice(0, 120);
    const project = (q.get("project") || "").replace(/[^a-z0-9-]/gi, "").slice(0, 80);
    if (about || project) {
      formData.value.message = [about && `I'd like a quote for: ${about}`, project && `Like your project /showcase/${project}`].filter(Boolean).join("\n");
      formData.value.requestQuote = true;
    }
  } catch (e) { /* no query */ }
  try{
    await getToken();
  }
  catch (error) {
    console.log("Failed to get security token:", error);
  }
});

// Name Validation
const validateName = () => {
  errors.value.name = formData.value.name.length >= 2 ? "" : "Name must be at least 2 characters long.";
};

// Contact Validation (Email or Phone)
const validateContact = () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[0-9\s+()-]+$/; // Allows numbers, spaces, +, (), and -

  if (!formData.value.contact) {
    errors.value.contact = "Phone or email is required.";
  } else if (!emailRegex.test(formData.value.contact) && !phoneRegex.test(formData.value.contact)) {
    errors.value.contact = "Enter a valid email or phone number.";
  } else {
    errors.value.contact = "";
  }
};

// Handle Form Submission
const handleSubmit = async () => {
  if (isPreview.value) {
    messageSubmitted.value = true;
    return;
  }
  validateName();
  validateContact();


  if (!errors.value.name && !errors.value.contact) {
    loading.value = true;

    try {
      //await getToken(); // Fetch a new token for the next submission
      const workMessage = `
      FROM PAGE: ${router.currentRoute.value.fullPath}

      ${formData.value.message}
      `
      const response = await axios.post("/api/contact/contact", {
        name: formData.value.name,
        businessName: formData.businessName,
        contact: formData.value.contact,
        requestQuote: formData.value.requestQuote,
        message: workMessage,
        token: token.value, // Include security token
      });

      formData.value = { name: "", contact: "", requestQuote: false };
      messageSubmitted.value = true;
    } catch (error) {
      console.error("Error submitting form:", error);
      errors.value.token = "There was an error submitting your request. Please try again.";
    } finally {
      loading.value = false;
    }
  }
};

// Dynamic styles using CSS variables for theming
const cssVars = computed(() => ({
  "--bg-light-variant-1": "var(--brand-whitest)",
  "--bg-light-variant-2": "var(--brand-lightest)",
  "--bg-dark-variant-1": "var(--brand-dark)",
  "--bg-dark-variant-2": "var(--brand-darkest)",
  "--bg-light": "var(--brand-lightest)",
  "--bg-dark": "var(--brand-darkest)",
  "--card-light-1": "var(--brand-whitest)",
  "--card-light-2": "var(--brand-lightest)",
  "--card-accent": "var(--brand-accent)",
  "--card-dark": "var(--brand-dark)",
  "--input-bg": 'white',
  "--text-primary": "var(--brand-text-primary, #333)",
  "--text-secondary": "var(--brand-text-secondary, #666)",
  "--border-color": "var(--brand-accent)",
  "--accent": "var(--brand-accent)"
}));

// Ensure form container contrasts properly
const computedCardClass = computed(() =>
    props.backgroundVariant === 2
        ? "bg-[var(--card-light-1)]/45 backdrop-hue-rotate-25 p-6 bg-backdrop-blur-sm shadow-lg rounded border-r-2 border-[var(--card-accent)]"
        : ""
);
</script>
